/**
 * SessionStart component - Entry point for participants to begin a session.
 *
 * Design: DENiZEN — matches Figma "Surface Pro 8 - 2" (node 1:48)
 * - Lives inside GreenPageLayout's stone card (no own background needed)
 * - DENiZEN logo centred at top, thin separator below
 * - Individual/group cards: rounded-[10px] border, checkbox indicator top-right
 * - CTA button centred at ~450px width (not full-width)
 * - WCAG 2.1 AA compliant
 */

import { useState } from 'react'

export interface SessionStartProps {
  /** Callback when session start is initiated */
  onStart: (sessionType: 'individual' | 'group' | 'ambient') => void
  /** Whether the component is in a loading state */
  isLoading?: boolean
  /** Error message to display */
  error?: string | null
  /** Whether to show the ambient recording option (only via event code join) */
  showAmbient?: boolean
}

export function SessionStart({ onStart, isLoading = false, error, showAmbient = false }: SessionStartProps) {
  const [selectedType, setSelectedType] = useState<'individual' | 'group' | 'ambient'>('individual')

  const handleStart = () => {
    if (!isLoading) {
      onStart(selectedType)
    }
  }

  return (
    <div className="px-8 py-6 sm:px-12 relative">

      {/* Logo header */}
      <header className="mb-6 animate-slide-up text-center">
        <img
          src="/images/brand/Denizen_1.png"
          alt="DENiZEN — Communities imagining futures together"
          className="w-64 mx-auto"
        />
      </header>

      {/* Separator — matches Figma line between logo and type selection */}
      <hr className="border-dark-green/15 mb-6" />

      {/* Session Type Selection */}
      <fieldset className="mb-8 animate-slide-up-delay-1">
        <legend className="w-full text-center text-base font-display font-semibold text-dark-green mb-5 tracking-wide">
          How would you like to participate?
        </legend>

        <div className={`grid grid-cols-1 ${showAmbient ? 'sm:grid-cols-3' : 'sm:grid-cols-2'} gap-4 items-stretch`}>

          {/* Individual Option */}
          <label className="relative cursor-pointer">
            <input
              type="radio"
              name="sessionType"
              value="individual"
              checked={selectedType === 'individual'}
              onChange={() => setSelectedType('individual')}
              className="sr-only"
              aria-describedby="individual-desc"
            />

            {/* Checkbox indicator — top-right corner, per Figma */}
            <div
              className={`
                absolute top-3 right-3 w-6 h-6 rounded-full border flex items-center justify-center
                transition-all duration-150
                ${selectedType === 'individual'
                  ? 'bg-[#a5f1ad] border-dark-green'
                  : 'bg-white border-dark-green'
                }
              `}
              aria-hidden="true"
            >
              {selectedType === 'individual' && (
                <svg className="w-3.5 h-3.5 text-dark-green" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
                </svg>
              )}
            </div>

            <div
              className={`
                h-full rounded-[10px] border p-6 text-center transition-all duration-150
                ${selectedType === 'individual'
                  ? 'border-dark-green'
                  : 'border-[#bbb09b] hover:border-dark-green/40'
                }
              `}
            >
              {/* Person icon — filled, mid-green */}
              <div className="flex justify-center mb-3">
                <svg
                  className="w-10 h-10 text-mid-green"
                  viewBox="0 0 24 24"
                  fill="currentColor"
                  aria-hidden="true"
                >
                  <path d="M12 12c2.7 0 4.8-2.1 4.8-4.8S14.7 2.4 12 2.4 7.2 4.5 7.2 7.2 9.3 12 12 12zm0 2.4c-3.2 0-9.6 1.6-9.6 4.8v2.4h19.2v-2.4c0-3.2-6.4-4.8-9.6-4.8z" />
                </svg>
              </div>

              <span className="block font-display font-semibold text-xl text-dark-green mb-2">
                Individual
              </span>
              <p id="individual-desc" className="text-sm font-body text-muted leading-relaxed">
                Share your personal vision in a one-on-one conversation
              </p>
            </div>
          </label>

          {/* Group Option */}
          <label className="relative cursor-pointer">
            <input
              type="radio"
              name="sessionType"
              value="group"
              checked={selectedType === 'group'}
              onChange={() => setSelectedType('group')}
              className="sr-only"
              aria-describedby="group-desc"
            />

            {/* Checkbox indicator — top-right corner, per Figma */}
            <div
              className={`
                absolute top-3 right-3 w-6 h-6 rounded-full border flex items-center justify-center
                transition-all duration-150
                ${selectedType === 'group'
                  ? 'bg-[#a5f1ad] border-dark-green'
                  : 'bg-white border-dark-green'
                }
              `}
              aria-hidden="true"
            >
              {selectedType === 'group' && (
                <svg className="w-3.5 h-3.5 text-dark-green" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
                </svg>
              )}
            </div>

            <div
              className={`
                h-full rounded-[10px] border p-6 text-center transition-all duration-150
                ${selectedType === 'group'
                  ? 'border-dark-green'
                  : 'border-[#bbb09b] hover:border-dark-green/40'
                }
              `}
            >
              {/* Group icon — filled, mid-green */}
              <div className="flex justify-center mb-3">
                <svg
                  className="w-10 h-10 text-mid-green"
                  viewBox="0 0 24 24"
                  fill="currentColor"
                  aria-hidden="true"
                >
                  <path d="M16 11c1.66 0 2.99-1.34 2.99-3S17.66 5 16 5c-1.66 0-3 1.34-3 3s1.34 3 3 3zm-8 0c1.66 0 2.99-1.34 2.99-3S9.66 5 8 5C6.34 5 5 6.34 5 8s1.34 3 3 3zm0 2c-2.33 0-7 1.17-7 3.5V19h14v-2.5c0-2.33-4.67-3.5-7-3.5zm8 0c-.29 0-.62.02-.97.05 1.16.84 1.97 1.97 1.97 3.45V19h6v-2.5c0-2.33-4.67-3.5-7-3.5z" />
                </svg>
              </div>

              <span className="block font-display font-semibold text-xl text-dark-green mb-2">
                Groups
              </span>
              <p id="group-desc" className="text-sm font-body text-muted leading-relaxed">
                Explore together with 2-4 people from your community
              </p>
            </div>
          </label>

          {/* Ambient Option — only shown when session started via event code */}
          {showAmbient && (
          <label className="relative cursor-pointer">
            <input
              type="radio"
              name="sessionType"
              value="ambient"
              checked={selectedType === 'ambient'}
              onChange={() => setSelectedType('ambient')}
              className="sr-only"
              aria-describedby="ambient-desc"
            />

            {/* Checkbox indicator — top-right corner, per Figma */}
            <div
              className={`
                absolute top-3 right-3 w-6 h-6 rounded-full border flex items-center justify-center
                transition-all duration-150
                ${selectedType === 'ambient'
                  ? 'bg-[#a5f1ad] border-dark-green'
                  : 'bg-white border-dark-green'
                }
              `}
              aria-hidden="true"
            >
              {selectedType === 'ambient' && (
                <svg className="w-3.5 h-3.5 text-dark-green" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
                </svg>
              )}
            </div>

            <div
              className={`
                h-full rounded-[10px] border p-6 text-center transition-all duration-150
                ${selectedType === 'ambient'
                  ? 'border-dark-green'
                  : 'border-[#bbb09b] hover:border-dark-green/40'
                }
              `}
            >
              {/* Microphone icon — filled, mid-green */}
              <div className="flex justify-center mb-3">
                <svg
                  className="w-10 h-10 text-mid-green"
                  viewBox="0 0 24 24"
                  fill="currentColor"
                  aria-hidden="true"
                >
                  <path d="M12 14c1.66 0 3-1.34 3-3V5c0-1.66-1.34-3-3-3S9 3.34 9 5v6c0 1.66 1.34 3 3 3z" />
                  <path d="M17 11c0 2.76-2.24 5-5 5s-5-2.24-5-5H5c0 3.53 2.61 6.43 6 6.92V21h2v-3.08c3.39-.49 6-3.39 6-6.92h-2z" />
                </svg>
              </div>

              <span className="block font-display font-semibold text-xl text-dark-green mb-2">
                Ambient
              </span>
              <p id="ambient-desc" className="text-sm font-body text-muted leading-relaxed">
                Record a group conversation during activities and extract community themes
              </p>
            </div>
          </label>
          )}

        </div>
      </fieldset>

      {/* Error Display */}
      {error && (
        <div
          className="mb-6 p-4 bg-error/10 border border-dark-green/10 rounded-2xl animate-shake"
          role="alert"
          aria-live="polite"
        >
          <div className="flex items-start gap-3">
            <div className="w-10 h-10 bg-error rounded-full flex items-center justify-center flex-shrink-0">
              <span className="text-white font-display font-bold text-xl">!</span>
            </div>
            <div>
              <p className="font-display font-semibold text-dark-green mb-1">Something went wrong</p>
              <p className="font-body text-dark-green text-sm">{error}</p>
            </div>
          </div>
        </div>
      )}

      {/* Start Button — centred, ~450px wide per Figma */}
      <div className="animate-slide-up-delay-2 flex justify-center">
        <button
          type="button"
          onClick={handleStart}
          disabled={isLoading}
          className="btn-denizen-primary w-full max-w-[450px] py-4"
          aria-busy={isLoading ? 'true' : 'false'}
        >
          {isLoading ? (
            <span className="flex items-center justify-center gap-3">
              <svg
                className="animate-spin h-6 w-6"
                xmlns="http://www.w3.org/2000/svg"
                fill="none"
                viewBox="0 0 24 24"
                aria-hidden="true"
              >
                <circle
                  className="opacity-25"
                  cx="12"
                  cy="12"
                  r="10"
                  stroke="currentColor"
                  strokeWidth="4"
                />
                <path
                  className="opacity-75"
                  fill="currentColor"
                  d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                />
              </svg>
              <span>Starting...</span>
            </span>
          ) : (
            <span className="flex items-center justify-center gap-3">
              <span>Begin your journey into the future</span>
              <svg
                className="w-6 h-6"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M13 7l5 5m0 0l-5 5m5-5H6" />
              </svg>
            </span>
          )}
        </button>
      </div>

    </div>
  )
}

export default SessionStart
